import React, { useEffect, useMemo, useState } from 'react';
import { format, addMonths, subMonths, startOfMonth, endOfMonth, eachDayOfInterval, getDay } from 'date-fns';

interface MobileDateTimePickerProps {
	isOpen: boolean;
	onClose: () => void;
	onConfirm: (date: Date | null) => void;
	initialDateTime?: Date | null;
}

const hours = Array.from({ length: 12 }, (_, i) => (i + 1).toString().padStart(2, '0'));
const minutes = Array.from({ length: 60 }, (_, i) => i.toString().padStart(2, '0'));

const MobileDateTimePicker: React.FC<MobileDateTimePickerProps> = ({ isOpen, onClose, onConfirm, initialDateTime }) => {
	const [currentMonth, setCurrentMonth] = useState<Date>(initialDateTime || new Date());
	const [selectedDate, setSelectedDate] = useState<Date | null>(initialDateTime || new Date());
	const [hour, setHour] = useState<string>(format(initialDateTime || new Date(), 'hh'));
	const [minute, setMinute] = useState<string>(format(initialDateTime || new Date(), 'mm'));
	const [isPM, setIsPM] = useState<boolean>(format(initialDateTime || new Date(), 'a') === 'PM');

	useEffect(() => {
		if (initialDateTime) {
			setCurrentMonth(initialDateTime);
			setSelectedDate(initialDateTime);
			setHour(format(initialDateTime, 'hh'));
			setMinute(format(initialDateTime, 'mm'));
			setIsPM(format(initialDateTime, 'a') === 'PM');
		}
	}, [initialDateTime]);

	const daysInMonth = useMemo(() => {
		const start = startOfMonth(currentMonth);
		const end = endOfMonth(currentMonth);
		return eachDayOfInterval({ start, end });
	}, [currentMonth]);

	const firstDayOffset = (date: Date) => {
		const d = startOfMonth(date);
		const day = getDay(d); // 0 (Sun) - 6 (Sat)
		return day === 0 ? 6 : day - 1; // make Monday first
	};

	const handleConfirm = () => {
		if (!selectedDate) {
			onConfirm(null);
			return;
		}
		const h12 = parseInt(hour, 10) % 12;
		const h24 = isPM ? h12 + 12 : h12;
		const result = new Date(selectedDate);
		result.setHours(h24);
		result.setMinutes(parseInt(minute, 10));
		result.setSeconds(0);
		result.setMilliseconds(0);
		onConfirm(result);
	};

	if (!isOpen) return null;

	return (
		<div className="fixed inset-0 z-[9999] bg-black bg-opacity-50 flex items-end justify-center">
			<div className="w-full max-w-md bg-white rounded-t-xl shadow-xl overflow-hidden">
				<div className="p-4 border-b flex items-center justify-between">
					<h3 className="text-lg font-medium">Select Date & Time</h3>
					<button onClick={onClose} className="text-gray-500 hover:text-gray-700">✕</button>
				</div>

				<div className="p-4">
					{/* Month Navigation */}
					<div className="flex items-center justify-between mb-3">
						<button onClick={() => setCurrentMonth(subMonths(currentMonth, 1))} className="px-2 py-1 rounded bg-gray-100">←</button>
						<div className="font-medium">{format(currentMonth, 'MMMM yyyy')}</div>
						<button onClick={() => setCurrentMonth(addMonths(currentMonth, 1))} className="px-2 py-1 rounded bg-gray-100">→</button>
					</div>

					{/* Weekday Headers */}
					<div className="grid grid-cols-7 text-center text-xs text-gray-500 mb-1">
						{['Mo','Tu','We','Th','Fr','Sa','Su'].map((d) => (<div key={d}>{d}</div>))}
					</div>

					{/* Calendar Grid */}
					<div className="grid grid-cols-7 gap-1">
						{Array.from({ length: firstDayOffset(currentMonth) }).map((_, idx) => (<div key={`e-${idx}`}></div>))}
						{daysInMonth.map((d) => {
							const isSelected = selectedDate && format(d, 'yyyy-MM-dd') === format(selectedDate, 'yyyy-MM-dd');
							return (
								<button
									key={d.toISOString()}
									onClick={() => setSelectedDate(d)}
									className={`py-2 text-sm rounded ${isSelected ? 'bg-primary text-white' : 'bg-gray-100 hover:bg-gray-200'}`}
								>
									{format(d, 'd')}
								</button>
							);
						})}
					</div>

					{/* Time Selectors */}
					<div className="mt-4 grid grid-cols-3 gap-2">
						<div className="max-h-40 overflow-y-auto border rounded">
							{hours.map((h) => (
								<button key={h} onClick={() => setHour(h)} className={`w-full py-2 text-center ${hour===h ? 'bg-primary text-white' : 'hover:bg-gray-100'}`}>{h}</button>
							))}
						</div>
						<div className="max-h-40 overflow-y-auto border rounded">
							{minutes.map((m) => (
								<button key={m} onClick={() => setMinute(m)} className={`w-full py-2 text-center ${minute===m ? 'bg-primary text-white' : 'hover:bg-gray-100'}`}>{m}</button>
							))}
						</div>
						<div className="grid grid-rows-2 gap-2">
							<button onClick={() => setIsPM(false)} className={`py-2 border rounded ${!isPM ? 'bg-primary text-white' : 'hover:bg-gray-100'}`}>AM</button>
							<button onClick={() => setIsPM(true)} className={`py-2 border rounded ${isPM ? 'bg-primary text-white' : 'hover:bg-gray-100'}`}>PM</button>
						</div>
					</div>
				</div>

				<div className="p-4 border-t flex justify-end gap-2">
					<button onClick={onClose} className="px-4 py-2 bg-gray-100 text-gray-800 rounded hover:bg-gray-200">Cancel</button>
					<button onClick={handleConfirm} className="px-4 py-2 bg-primary text-white rounded hover:bg-primary-dark">Confirm</button>
				</div>
			</div>
		</div>
	);
};

export default MobileDateTimePicker;
